// src/contexts/CategoryContext.tsx
import React, { createContext, useContext, ReactNode } from 'react';
import { CategoryService, Category } from '../Services/categoryService';
import { useAllCategoriesQuery, useInvalidateCategories } from '../Hooks/useCategoryQuery';

interface CategoryContextType {
  categories: Category[];
  activeCategories: Category[];
  parentCategories: Category[];
  activeParentCategories: Category[];
  starredActiveParentCategories: Category[];
  bannerCategories: Category[];
  categoriesWithSubcategories: (Category & { subcategories: Category[] })[];
  activeCategoriesWithSubcategories: (Category & { subcategories: Category[] })[];
  loading: boolean;
  error: string | null;
  refreshCategories: () => Promise<void>;
  getCategoryById: (id: string) => Promise<Category | null>;
  getSubcategoriesByParentId: (parentId: string) => Category[];
  getActiveSubcategoriesByParentId: (parentId: string) => Category[];
  createCategory: (category: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateCategory: (id: string, category: Partial<Omit<Category, 'id' | 'createdAt' | 'createdBy'>>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  deactivateCategory: (id: string) => Promise<void>;
  activateCategory: (id: string) => Promise<void>;
  toggleStarredCategory: (id: string, isStarred: boolean) => Promise<void>;
}

const CategoryContext = createContext<CategoryContextType | undefined>(undefined);

export const useCategories = () => {
  const context = useContext(CategoryContext);
  if (context === undefined) {
    throw new Error('useCategories must be used within a CategoryProvider');
  }
  return context;
};

interface CategoryProviderProps {
  children: ReactNode;
}

// Derive bannerCategories from a flat list in memory.
// Mirrors getBannerCategories() logic: starred first, then non-starred to fill up to maxCount.
function deriveBannerCategories(all: Category[], maxCount = 6): Category[] {
  const activeParents = all.filter(c => c.parentId === null && c.isActive);
  const starred = activeParents.filter(c => c.isStarred);
  if (starred.length >= maxCount) return starred.slice(0, maxCount);
  const nonStarred = activeParents.filter(c => !c.isStarred);
  return [...starred, ...nonStarred].slice(0, maxCount);
}

export const CategoryProvider: React.FC<CategoryProviderProps> = ({ children }) => {
  const { data: allData, isLoading, isError, error, refetch } = useAllCategoriesQuery();
  const invalidateAll = useInvalidateCategories();

  // All derived lists computed in memory from the single cached fetch.
  // Original context made 8 parallel Firestore reads; now it's 1.
  const categories: Category[] = allData ?? [];
  const activeCategories = categories.filter(c => c.isActive);
  const parentCategories = categories.filter(c => c.parentId === null);
  const activeParentCategories = categories.filter(c => c.parentId === null && c.isActive);
  const starredActiveParentCategories = categories.filter(c => c.parentId === null && c.isActive && c.isStarred);
  const bannerCategories = deriveBannerCategories(categories);

  const categoriesWithSubcategories = parentCategories.map(parent => ({
    ...parent,
    subcategories: categories.filter(c => c.parentId === parent.id),
  }));

  const activeCategoriesWithSubcategories = activeParentCategories.map(parent => ({
    ...parent,
    subcategories: activeCategories.filter(c => c.parentId === parent.id),
  }));

  const refreshCategories = async () => {
    await refetch();
  };

  const getCategoryById = async (id: string): Promise<Category | null> => {
    const cached = categories.find(c => c.id === id);
    if (cached) return cached;
    return CategoryService.getCategoryById(id);
  };

  const getSubcategoriesByParentId = (parentId: string): Category[] =>
    categories.filter(c => c.parentId === parentId);

  const getActiveSubcategoriesByParentId = (parentId: string): Category[] =>
    activeCategories.filter(c => c.parentId === parentId);

  // Write operations: call service then invalidate cache so React Query
  // refetches automatically on the next render — same pattern as admin modules.
  const createCategory = async (
    category: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> => {
    const id = await CategoryService.createCategory(category);
    invalidateAll();
    return id;
  };

  const updateCategory = async (
    id: string,
    category: Partial<Omit<Category, 'id' | 'createdAt' | 'createdBy'>>
  ): Promise<void> => {
    await CategoryService.updateCategory(id, category);
    invalidateAll();
  };

  const deleteCategory = async (id: string): Promise<void> => {
    await CategoryService.deleteCategory(id);
    invalidateAll();
  };

  const deactivateCategory = async (id: string): Promise<void> => {
    await CategoryService.deactivateCategory(id);
    invalidateAll();
  };

  const activateCategory = async (id: string): Promise<void> => {
    await CategoryService.activateCategory(id);
    invalidateAll();
  };

  const toggleStarredCategory = async (id: string, isStarred: boolean): Promise<void> => {
    await CategoryService.toggleStarredCategory(id, isStarred);
    invalidateAll();
  };

  const value: CategoryContextType = {
    categories,
    activeCategories,
    parentCategories,
    activeParentCategories,
    starredActiveParentCategories,
    bannerCategories,
    categoriesWithSubcategories,
    activeCategoriesWithSubcategories,
    loading: isLoading,
    error: isError ? (error as Error)?.message ?? 'Failed to load categories' : null,
    refreshCategories,
    getCategoryById,
    getSubcategoriesByParentId,
    getActiveSubcategoriesByParentId,
    createCategory,
    updateCategory,
    deleteCategory,
    deactivateCategory,
    activateCategory,
    toggleStarredCategory,
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
};